import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/src/layer/VectorImage';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import * as olColor from 'ol/color';
import { colors, mapConfig } from '../../../utils/config';

// set variables to update layer style paint function
let layer = null;
let source = null;
let lastLines = null;
let data = null;
let now = new Date();
const noMatchColor = olColor.asString([0, 0, 0, 0]);

const geojsonFormat = new GeoJSON({
	dataProjection: 'EPSG:4326',
	featureProjection: 'EPSG:3857',
});

export default async function loadLines(map, lines, typeFilter, zoomLevel) {
	if (!map && !layer) {
		// Neither map nor layer is defined. Cannot load lines.
		return;
	}

	if (!map) {
		// remove lines if zoomed out
		if (zoomLevel < mapConfig.minZoomLevel.stops + mapConfig.minZoom) {
			layer.setVisible(false);
			layer.set('newLines', lines);
			return;
		}

		checkFilter(typeFilter);
		// if layer is set set lines as value and force repaint to get layer function
		if (layer && lines !== lastLines) {
			layer.set('newLines', lines);
			// Do NOT call source.refresh() here: this VectorSource is created with static,
			// in-memory `features` (no `url`/loader). Calling refresh() clears the source's
			// features and relies on a loader to repopulate them, which we don't have, so the
			// lines would disappear permanently. Instead, just force the layer to re-render so
			// the style function re-evaluates against the updated `newLines` filter.
			layer.changed();
		}
		lastLines = lines;
		return;
	}
	lastLines = lines;

	if (data === null) {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_LINES_HOST || ''}/geojson/lines/lines-current.geojson`,
			);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			data = await response.json();
		} catch (error) {
			console.error('Error fetching lines geojson:', error);
			return;
		}
	}

	now = new Date();
	// Only keep features that are valid for the current date.
	// Keep first line of each line id only.
	const features = geojsonFormat.readFeatures(data).filter(inRangeFilter());

	source = new VectorSource({
		features,
	});
	const style2 = new Style({
		fill: new Fill({
			color: '#fc03c2',
		}),
		stroke: new Stroke({
			color: '#fc03c2',
			width: 3,
		}),
	});
	layer = new VectorLayer({
		background: '#1a2b39',
		source,
		style: function style(feature) {
			// set alpha 0 if no match
			const color = getLineColor(feature.get('text'), feature?.values_?.textDiva);
			if (color === colors.sbahn) {
				style2.setZIndex(2);
			} else {
				style2.setZIndex(1);
			}
			let isMatch = false;
			const newLines = layer.get('newLines');
			// if newlines is null there is no filter so show all lines and return
			if (!newLines) {
				if (color) {
					style2.getStroke().setColor(color);
				}
				return style2;
			}

			// textDiva matches part of line id
			if (newLines[parseInt(`${feature.get('textDiva')}`, 10)] !== undefined) {
				isMatch = true;
			} else if (newLines[feature.get('line')] !== undefined) {
				isMatch = true;
			} else if (newLines[feature.get('id')] !== undefined) {
				isMatch = true;
			}
			if (isMatch) {
				if (color) {
					style2.getStroke().setColor(color);
				}
			} else {
				style2.getStroke().setColor(noMatchColor);
			}
			return style2;
		},
	});
	layer.set('name', 'line2');
	map.addLayer(layer);
	checkFilter(typeFilter);
}

// if Haltestellen aren't visible in filter don't show lines
function checkFilter(typeFilter) {
	let isFiltered = true;
	if (typeFilter?.stops?.Linien && typeFilter?.stops?.Linien !== 0) {
		isFiltered = false;
	}

	layer.setVisible(!isFiltered);
}

function inRangeFilter() {
	const existingLines = [];

	return function filter(feature) {
		const line = feature.get('line');
		if (existingLines.includes(line)) {
			return false;
		}

		const validFrom = parseDate(feature.get('validFrom'));

		if (validFrom > now) {
			return false;
		}

		const validUntil = parseDate(feature.get('validUntil'));

		if (validUntil === null) {
			existingLines.push(line);
			return true;
		}

		validUntil.setHours(23, 59, 59, 999); // Set to the end of the day

		if (validUntil < now) {
			return false;
		}

		existingLines.push(line);
		return true;
	};
}

function parseDate(dateString) {
	if (typeof dateString !== 'string') {
		return null;
	}

	const parsedDate = new Date(dateString);
	if (parsedDate.toString() !== 'Invalid Date') {
		parsedDate.setHours(0, 0, 0, 0); // Set to the start of the day
		return parsedDate;
	}

	const dateArray = dateString.split('.');
	// check if date array is valid
	// handles only dates with format dd.mm.yyyy
	if (Array.isArray(dateArray) && dateArray.length === 3) {
		// dd.mm.yyyy -> new Date(year, monthIndex, day)
		// year -> index 2
		// month -> index 1, as month begins with zero parse to int and subtract 1
		// dd -> index 0
		return new Date(
			parseInt(dateArray[2], 10),
			parseInt(dateArray[1], 10) - 1,
			parseInt(dateArray[0], 10),
			0,
			0,
			0,
			0,
		);
	}

	return null;
}

const getLineColor = (name, textDiva = '', defaultValue = false) => {
	// type = zacke
	if (name === '10' && textDiva === '21010_') {
		return colors.ubahn; // '#fcf403';
	}
	// type = seilbahn
	if (name === '20' && textDiva === '21020_') {
		return colors.ubahn; // '#fcf403';
	}
	// type = ubahn
	if (name.match(/^U\d+([AEae])?$/)) {
		return colors.ubahn; // '#0320fc';
	}
	// type = sbahn
	if (name.match(/^S\d+([AEae])?$/)) {
		return colors.sbahn; // '#03fc67';
	}
	// type = rbahn
	if (name.match(/^(IRE|R|RB|RE|MEX)\d+([AEae])?$/) || name.match(/^R-Bahn /)) {
		return colors.rbahn; // '#8a8a8a';
	}
	// type = taxi
	if (name.match(/^(RT|LT)\d+$/)) {
		return colors.bus; // '#8a8a8a';
	}
	// type = bus
	if (name.match(/^((N|X|BB)?\d+(\.\d+|[AaE])?|E)$/)) {
		return colors.bus; // '#fc0303';
	}
	return defaultValue;
};
